import {
  BREED_AFFILIATE_FIELDS,
  DISCIPLINE_AFFILIATE_FIELDS,
  DISCIPLINE_MEMBERSHIP_FIELDS,
  getHorseNumberFieldName,
  getUserFieldName,
  NumberMembershipFieldKey,
} from '../../../../../const/discipline-memberships-const'
import { RegistrationFeesType } from '../../../../../models/event-fees/event-fees.interface'
import { IHorseData } from '../../../../../models/horse/horse.interface'
import { IRegistrationByDayInterface } from '../../../../../models/registrations-by-day/registrationByDay.interface'
import { IScoreCardInterface } from '../../../../../models/score-cards/score-cards.interface'
import { IUserDocuments, IUserInterface } from '../../../../../models/users/user.interface'
import { hasBeenScored } from './components/EventResultsReport/customDataExportUtils'

/**
 * Parses address strings into structured components for US and Canadian addresses.
 *
 * Supports standard formats from North America. For unsupported formats, returns null
 * to allow fallback handling (e.g., putting the entire address in the street field).
 *
 * @param address - A string containing an address to parse
 * @returns An object with parsed address components (street, city, state, zip)
 *          or null if the address doesn't match any supported format
 *
 * @example
 * US address
 * parseAddress("123 Main St, Springfield, IL 62701")
 * Returns: { street: "123 Main St", city: "Springfield", state: "IL", zip: "62701" }
 *
 * @example
 * Canadian address
 * parseAddress("231 Ontario St, Toronto, ON M5A 2V6, Canada")
 * Returns: { street: "231 Ontario St", city: "Toronto", state: "ON", zip: "M5A2V6" }
 *
 * @example
 * Unsupported format (triggers fallback)
 * parseAddress("10 Downing Street, London, SW1A 2AA, UK")
 * Returns: null
 */
export function parseAddress(address: string) {
  if (!address?.trim()) {
    return null
  }

  const addr = address.trim()

  // US format: "street, city, ST 12345"
  const usMatch = addr.match(/^(.*),\s*(.*),\s*([A-Z]{2})\s*(\d{5}(?:-\d{4})?)(?:,\s*USA)?$/)
  if (usMatch) {
    return {
      street: usMatch[1].trim(),
      city: usMatch[2].trim(),
      state: usMatch[3].trim(),
      zip: usMatch[4].trim(),
    }
  }

  // canadian format: "street, city, PROV A1A 1A1"
  const caMatch = addr.match(
    /^(.*),\s*(.*),\s*([A-Z]{2})\s*([A-Z]\d[A-Z]\s*\d[A-Z]\d)(?:,\s*Canada)?$/i
  )
  if (caMatch) {
    return {
      street: caMatch[1].trim(),
      city: caMatch[2].trim(),
      state: caMatch[3].trim(),
      zip: caMatch[4].trim().replace(/\s/g, '').toUpperCase(),
    }
  }

  return null
}

/**
 * Creates a mapping between class names and their associated score sheets.
 *
 * Not all registrationsByDay objects have a score.sheet, but score sheets are
 * assigned at the class level. This function creates a reliable mapping of
 * classes to score sheets and score sheet types instead of relying on data
 * in the registrationsByDay objects for each registration.
 *
 * @param registrationFees - Array of registration fee objects used to extract unique class names
 * @param registrationsByDay - Array of registration by day objects containing score sheet information
 * @returns An object mapping class names to their score sheet name and type
 *
 * @example
 * Given:
 * registrationFees = [{ name: 'Some Class' }]
 * registrationsByDay = [{
 *   registrationByDayName: 'Some Class',
 *   score: { sheet: { name: 'Standard Score Sheet', type: 'hunter' } }
 * }]
 *
 * Returns:
 * { 'Some Class': { scoreSheetName: 'Standard Score Sheet', scoreSheetType: 'hunter' } }
 */
export function createClassToScoreSheetMapping(
  registrationFees: RegistrationFeesType[],
  registrationsByDay: IRegistrationByDayInterface[]
) {
  // get all unique class names from registrationFees
  const uniqueClassNames = registrationFees
    .map((fee: RegistrationFeesType) => fee.name)
    .filter(Boolean)

  // mapping object
  const classToScoreSheetMap: Record<
    string,
    { scoreSheetName: string; scoreSheetType: string | null }
  > = {}

  // loop through registrationsByDay to find score sheets for each class
  registrationsByDay.forEach((registration: IRegistrationByDayInterface) => {
    const className = registration.registrationByDayName

    // skip if className null / undefined
    if (!className) {
      return
    }

    // skip if this class isn't in our list or we already found a mapping for it
    if (!uniqueClassNames.includes(className) || classToScoreSheetMap[className]) {
      return
    }

    // check if this registration has a defined score sheet
    if (registration.score?.sheet?.name && registration.score.sheet.name !== '') {
      // map the class name to the score sheet name
      classToScoreSheetMap[className] = {
        scoreSheetName: registration.score.sheet.name,
        scoreSheetType: registration.score.sheet.type,
      }
    }
  })

  return classToScoreSheetMap
}

/**
 * Converts a score card name to its corresponding fence type code.
 * Used in USEF export functionality.
 *
 * @param scoreCardName - The name of the score card to convert
 * @param hunterJumperScoreCards - Array of score cards that should receive the "M" fence type for hunter-jumper
 * @returns A single character code representing the fence type ('E' for Equitation, 'H' for Hunters, or empty string for all score cards that do not need special formatting in the export)
 */
export const getFenceTypeByScoreCardName = (
  scoreCardName: string,
  hunterJumperScoreCards: IScoreCardInterface[]
) => {
  const isInHunterJumperArr = hunterJumperScoreCards.some(
    (scoreCard) => scoreCard.id === scoreCardName
  )

  if (isInHunterJumperArr) {
    return 'M'
  }

  switch (scoreCardName) {
    case 'Equitation':
      return ''
    case 'Hunters':
      return 'E'
    default:
      return ''
  }
}

/**
 * Creates a mapping between class names and their associated fence types.
 * This mapping is derived from each registration fee's defined score card.
 *
 * @param registrationFees - Array of registration fee objects
 * @param hunterJumperScoreCards - Array of score cards that should receive the "M" designation
 * @returns An object where keys are class names and values contain scoreCard name and
 *          fence type code (derived from the scoreCard)
 *
 * @example
 * Given registration fees:
 * [{ name: 'Some Class', scoreCard: 'Equitation' }]
 * Returns:
 * { 'Some Class': { scoreCard: 'Equitation', fenceType: 'E' } }
 */
export const createFenceTypeClassMapping = (
  registrationFees: RegistrationFeesType[],
  hunterJumperScoreCards: IScoreCardInterface[]
) => {
  const classToFenceTypeMap: Record<string, { scoreCard: string; fenceType: string }> = {}

  if (!registrationFees || !Array.isArray(registrationFees)) {
    return classToFenceTypeMap // return empty mapping if registrationFees is invalid
  }

  registrationFees.forEach((fee: RegistrationFeesType) => {
    if (!fee.name) return

    classToFenceTypeMap[fee.name] = {
      scoreCard: fee.scoreCard || '',
      fenceType: getFenceTypeByScoreCardName(fee.scoreCard || '', hunterJumperScoreCards || []),
    }
  })
  return classToFenceTypeMap
}

/**
 * Finds the fence height raw value associated with a given score card name.
 *
 * @param scoreCardName - The name of the score card to search for
 * @param hunterJumperScoreCards - Array of score card objects to search through
 * @returns The fence height string if found, or an empty string if not found or if no scoreCardName is provided
 *
 * @example
 * findFenceHeightByScoreCard('Novice Hunter', scoreCards)
 *   Returns: '2'6"' (if that height exists in the matching score card)
 *
 * @example
 * findFenceHeightByScoreCard('', scoreCards)
 *   Returns: ''
 */
export const findFenceHeightByScoreCard = (
  scoreCardName: string,
  hunterJumperScoreCards: IScoreCardInterface[]
) => {
  if (!scoreCardName) return ''

  const scoreCardMetadata = hunterJumperScoreCards?.find((card) => {
    return card.name === scoreCardName
  })

  return scoreCardMetadata?.fenceHeight || ''
}

/**
 * Converts various fence height formats to a standardized display format.
 *
 * This function handles multiple fence height formats:
 * - Named designations (N - National, H - High Performance, etc.)
 * - Imperial measurements (e.g., 3'3")
 * - Metric measurements (e.g., 1.55m)
 *
 * For imperial measurements, it converts to total inches.
 * For metric measurements, it extracts just the numeric value.
 *
 * @param rawFenceHeight - The fence height string to convert
 * @returns A standardized display format string:
 *          - Single letter for named designations (N, H, A, R)
 *          - "0" for "Poles"
 *          - Total inches for imperial measurements
 *          - Numeric value only for metric measurements
 *          - Original string if format is unrecognized
 *
 * @example
 * convertFenceHeightToDisplay('N - National')
 *   Returns: 'N'
 *
 * @example
 * convertFenceHeightToDisplay('3\'6"')
 *   Returns: '42'
 *
 * @example
 * convertFenceHeightToDisplay('1.20m')
 *   Returns: '1.20'
 */
export const convertFenceHeightToDisplay = (rawFenceHeight: string) => {
  if (!rawFenceHeight) return ''

  switch (rawFenceHeight) {
    case 'N - National':
      return 'N'
    case 'H - High Performance':
      return 'H'
    case 'A - American':
      return 'A'
    case 'R - Regional':
      return 'R'
    case 'Poles':
      return '0'
    default:
      // check if rawFenceHeight is imperial (e.g., 3'3")
      if (rawFenceHeight.includes('’')) {
        // extract feet and inches
        const imperialRegex = /(\d+)’(\d+)”/
        const match = rawFenceHeight.match(imperialRegex)

        if (match) {
          const feet = parseInt(match[1], 10)
          const inches = parseInt(match[2], 10)

          // convert to total inches
          const totalInches = feet * 12 + inches

          return totalInches.toString()
        }
      }

      // Check if rawFenceHeight is metric (e.g., 1.55m)
      if (rawFenceHeight.includes('m')) {
        // extract the number part
        const metricRegex = /(\d+\.?\d*)/
        const match = rawFenceHeight.match(metricRegex)

        if (match) {
          return match[1] // return just the number part
        }
      }

      //fallback if can't parse - return raw
      return rawFenceHeight
  }
}

/**
 * Calculates stats for all classes based on registration data, aggregated by class.
 *
 * @param registrations - Array of registration objects
 * @returns A Map where keys are class UUIDs and values are objects containing:
 *          - entries: Total number of registrations for the class
 *          - competed: Number of non-scratched registrations with scores
 *          - completed: Number of non-scratched registrations with scores but no scoreStatus
 *
 * @example
 * const registrations = [
 *   { uuid: 'class-1', isScratched: false, score: { totalPoints: 15 } },
 *   { uuid: 'class-1', isScratched: true, score: { totalPoints: null } },
 *   { uuid: 'class-2', isScratched: false, score: { totalPoints: null } }
 * ]
 *
 * const stats = calculateClassStatistics(registrations)
 * stats.get('class-1') // Returns: { entries: 2, competed: 1, completed: 1 }
 * stats.get('class-2') // Returns: { entries: 1, competed: 0, completed: 0 }
 */
export const calculateClassStatistics = (registrations: IRegistrationByDayInterface[]) => {
  const classStatsMap = new Map<string, { entries: number; competed: number; completed: number }>()

  registrations.forEach((registration) => {
    const classUuid = registration.uuid
    if (!classUuid) return

    if (!classStatsMap.has(classUuid)) {
      classStatsMap.set(classUuid, { entries: 0, competed: 0, completed: 0 })
    }

    const stats = classStatsMap.get(classUuid)!

    // all registrations count as entries
    stats.entries++

    // skip scratched
    if (registration.isScratched) {
      return
    }

    // count competed (not scratched and have score)
    if (hasBeenScored(registration)) {
      stats.competed++

      // count completed (competed but no disqualifying score status)
      let hasScoreStatus = false

      if (registration.score?.scoreStatus) hasScoreStatus = true

      if (!hasScoreStatus) {
        stats.completed++
      }
    }
  })

  return classStatsMap
}

/**
 * Gets breed/registration affiliate numbers for a horse (USEF report format)
 */
export const getHorseBreedAffiliateNumbers = (horse: IHorseData): string => {
  const membershipNumbers = DISCIPLINE_MEMBERSHIP_FIELDS.filter((field) =>
    BREED_AFFILIATE_FIELDS.includes(field.key)
  )
    .map((field) => {
      const fieldName = getHorseNumberFieldName(field.key)
      const number = horse[fieldName as keyof IHorseData]
      return number && String(number).trim() !== '' ? `${field.key}:${number}` : null
    })
    .filter(Boolean)

  return membershipNumbers.join(', ')
}

/**
 * Gets discipline affiliate numbers for a horse (USEF report format)
 */
export const getHorseDisciplineAffiliateNumbers = (horse: IHorseData): string => {
  const membershipNumbers = DISCIPLINE_MEMBERSHIP_FIELDS.filter((field) =>
    DISCIPLINE_AFFILIATE_FIELDS.includes(field.key as any)
  )
    .map((field) => {
      const fieldName = getHorseNumberFieldName(field.key)
      const number = horse[fieldName as keyof IHorseData]
      return number && String(number).trim() !== '' ? `${field.key}:${number}` : null
    })
    .filter(Boolean)

  return membershipNumbers.join(', ')
}

/**
 * Gets user affiliate numbers for USEF report format (no expiration dates)
 * @param user - User interface object
 * @param affiliateFields - Array of affiliate field keys to filter by
 * @returns Formatted string of affiliate numbers
 */
export const getUserAffiliateNumbers = (
  user: IUserInterface,
  affiliateFields: typeof BREED_AFFILIATE_FIELDS | typeof DISCIPLINE_AFFILIATE_FIELDS
): string => {
  // gen discipline fields filtered by provided affiliate fields
  const filteredFields = DISCIPLINE_MEMBERSHIP_FIELDS.filter((field) =>
    affiliateFields.includes(field.key)
  ).map((field) => ({
    key: field.key,
    numberField: getUserFieldName(field.key) as NumberMembershipFieldKey,
  }))

  // get non-empty affiliate numbers without expiration
  const membershipNumbers = filteredFields
    .map((field) => {
      const membershipObj = user[field.numberField]
      if (!membershipObj) return null

      // check if membershipObj is an obj with documentNumber property - IUserDocuments type
      if (typeof membershipObj === 'object' && membershipObj !== null) {
        const typedMembershipObj = membershipObj as IUserDocuments
        const number = typedMembershipObj.documentNumber

        if (!number) return null

        // format without expiration for USEF report
        return `${field.key}:${number}`
      }
      return null
    })
    .filter(Boolean) as string[]

  return membershipNumbers.join(', ')
}

export const getRiderStatus = (registrationByDay: IRegistrationByDayInterface) => {
  let rideStatus = 'Entered'
  if (registrationByDay.isScratched) {
    rideStatus = 'Scratched'
  } else if (registrationByDay.score?.scoreStatus) {
    rideStatus = registrationByDay.score?.scoreStatus
  } else if (hasBeenScored(registrationByDay)) {
    rideStatus = 'Completed'
  }
  return rideStatus
}
